import React, { useState } from 'react';
import { X, Copy, CheckCircle2, FileText, Receipt, QrCode, ExternalLink, Barcode, BadgePercent } from 'lucide-react';
import { brl, dataBr, normalizeMesReferencia } from './format';
import { valorCobradoFatura, linkBoletoFatura } from './format';

// Portal do Consumidor — modal "Pagar" de uma fatura (abas PIX / Boleto).
// Os dados vêm todos da própria linha de faturas (codigo_pix_boleto EMV do Sicoob,
// url_img_codigo_pix_boleto, linha_digitavel_boleto, url_boleto), preenchidos pelo
// fluxo interno de emissão — aqui é só exibição + copiar.

/** Faixas de desconto por pagamento antecipado (desc_quitacao_* de boletos_vigor). */
export interface BoletoQuitacao {
    tem_desconto_quitacao?: boolean | null;
    desc_quitacao_data_pgt_1?: string | null;
    desc_quitacao_desconto_percent_1?: number | null;
    desc_quitacao_desconto_valor_1?: number | null;
    desc_quitacao_a_pagar_1?: number | null;
    desc_quitacao_data_pgt_2?: string | null;
    desc_quitacao_desconto_percent_2?: number | null;
    desc_quitacao_desconto_valor_2?: number | null;
    desc_quitacao_a_pagar_2?: number | null;
    desc_quitacao_data_pgt_3?: string | null;
    desc_quitacao_desconto_percent_3?: number | null;
    desc_quitacao_desconto_valor_3?: number | null;
    desc_quitacao_a_pagar_3?: number | null;
}

export interface FaixaQuitacao {
    pagueAte: string;
    aPagar: number | null;
    percent: number | null; // inteiro: 33 = 33%
    valor: number | null;
}

/** Extrai as faixas ainda válidas (data >= hoje), em ordem de data. */
export const faixasQuitacao = (boleto?: BoletoQuitacao | null): FaixaQuitacao[] => {
    if (!boleto || !boleto.tem_desconto_quitacao) return [];
    const hoje = new Date().toISOString().slice(0, 10);
    const faixas: FaixaQuitacao[] = [];
    ([1, 2, 3] as const).forEach((n) => {
        const b = boleto as Record<string, unknown>;
        const data = b[`desc_quitacao_data_pgt_${n}`] as string | null | undefined;
        const aPagar = b[`desc_quitacao_a_pagar_${n}`] as number | null | undefined;
        const percent = b[`desc_quitacao_desconto_percent_${n}`] as number | null | undefined;
        const valor = b[`desc_quitacao_desconto_valor_${n}`] as number | null | undefined;
        if (data && data >= hoje && (aPagar || percent || valor)) {
            faixas.push({ pagueAte: data, aPagar: aPagar ?? null, percent: percent ?? null, valor: valor ?? null });
        }
    });
    return faixas.sort((a, b) => a.pagueAte.localeCompare(b.pagueAte));
};

export interface FaturaPagamento {
    mes_referencia: string;
    uc_codigo: string;
    codigo_pix_boleto?: string | null;
    url_img_codigo_pix_boleto?: string | null;
    linha_digitavel_boleto?: string | null;
    url_boleto?: string | null;
    /** Boleto único publicado no site (PDF da parceira/Vigor com a Equatorial junto). */
    url_boleto_combinado?: string | null;
    pdf_fatura?: string | null;
    valor_boleto_vigor?: number | null;
    /** Valor do boleto único (energia + Equatorial) — é o que o cliente paga. */
    valor_emissao_boleto_unico?: number | null;
    valor_fatura_distribuidora?: number | null;
    vencimento_boleto?: string | null;
    data_vencimento?: string | null;
}

const CopiarBox = ({ valor, rotulo }: { valor: string; rotulo: string }) => {
    const [copiado, setCopiado] = useState(false);

    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(valor);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2500);
        } catch {
            // Clipboard indisponível (http/permissão): seleciona o texto pro usuário copiar
            window.getSelection()?.selectAllChildren(document.getElementById(`copiar-${rotulo}`) as Node);
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 border-2 border-dashed border-primary/30 bg-primary/5 rounded-xl px-4 py-3">
                <span id={`copiar-${rotulo}`} className="flex-1 text-sm text-gray-700 truncate font-mono">
                    {valor}
                </span>
                <button onClick={copiar} className="shrink-0 text-primary hover:text-primary/70 transition-colors" title={`Copiar ${rotulo}`}>
                    <Copy size={18} />
                </button>
            </div>
            <button
                onClick={copiar}
                className={`w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-full text-sm font-bold text-white transition-all transform active:scale-[0.98] ${copiado ? 'bg-green-600' : 'bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25'}`}
            >
                {copiado ? (
                    <>
                        <CheckCircle2 size={18} />
                        Código copiado!
                    </>
                ) : (
                    <>
                        <Copy size={18} />
                        Copiar código
                    </>
                )}
            </button>
        </div>
    );
};

const PagamentoModal = ({ fatura, quitacao, onClose }: { fatura: FaturaPagamento; quitacao?: BoletoQuitacao | null; onClose: () => void }) => {
    const temPix = typeof fatura.codigo_pix_boleto === 'string' && fatura.codigo_pix_boleto.length > 0;
    const linkBoleto = linkBoletoFatura(fatura);
    const temBoleto = (typeof fatura.linha_digitavel_boleto === 'string' && fatura.linha_digitavel_boleto.length > 0)
        || !!linkBoleto;

    const [aba, setAba] = useState<'pix' | 'boleto'>(temPix ? 'pix' : 'boleto');

    // #6117: no boleto único o valor pago é energia + Equatorial, não só a energia.
    const valor = valorCobradoFatura(fatura) ?? fatura.valor_fatura_distribuidora;
    const vencimento = fatura.vencimento_boleto || fatura.data_vencimento;
    const faixas = faixasQuitacao(quitacao);

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4 animate-fade-in" onClick={onClose}>
            <div
                className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 pt-6 pb-4 flex items-start justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Pagamento</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Referência {normalizeMesReferencia(fatura.mes_referencia)} · UC {fatura.uc_codigo}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                        <X size={22} />
                    </button>
                </div>

                {/* Valor + vencimento */}
                <div className="mx-6 mb-5 rounded-2xl bg-primary/5 border border-primary/15 px-5 py-4 flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-gray-400 uppercase">Valor a pagar</p>
                        <p className="text-2xl font-bold text-gray-900">{brl(valor)}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs font-medium text-gray-400 uppercase">Vencimento</p>
                        <p className="text-sm font-semibold text-gray-800">{dataBr(vencimento)}</p>
                    </div>
                </div>

                {/* Desconto por pagamento antecipado */}
                {faixas.length > 0 && (
                    <div className="mx-6 mb-5 rounded-2xl bg-green-50 border border-green-200 px-5 py-4">
                        <p className="flex items-center gap-2 text-sm font-bold text-green-800 mb-2">
                            <BadgePercent size={17} />
                            Pague antecipado e economize
                        </p>
                        <ul className="space-y-1.5">
                            {faixas.map((faixa) => (
                                <li key={faixa.pagueAte} className="text-sm text-green-800 flex items-baseline justify-between gap-3">
                                    <span>Até <span className="font-semibold">{dataBr(faixa.pagueAte)}</span></span>
                                    <span className="font-bold">
                                        {faixa.aPagar !== null ? brl(faixa.aPagar) : ''}
                                        {(faixa.percent !== null || faixa.valor !== null) && (
                                            <span className="font-semibold text-green-600 text-xs ml-1.5">
                                                ({faixa.percent !== null ? `−${faixa.percent.toLocaleString('pt-BR')}%` : `−${brl(faixa.valor)}`})
                                            </span>
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Abas PIX / Boleto */}
                <div className="px-6">
                    <div className="flex border-b border-gray-200">
                        {temPix && (
                            <button
                                onClick={() => setAba('pix')}
                                className={`flex-1 flex items-center justify-center gap-2 pb-3 text-sm font-bold transition-colors border-b-2 -mb-px ${aba === 'pix' ? 'text-primary border-primary' : 'text-gray-400 border-transparent hover:text-gray-600'}`}
                            >
                                <QrCode size={17} />
                                PIX
                            </button>
                        )}
                        {temBoleto && (
                            <button
                                onClick={() => setAba('boleto')}
                                className={`flex-1 flex items-center justify-center gap-2 pb-3 text-sm font-bold transition-colors border-b-2 -mb-px ${aba === 'boleto' ? 'text-primary border-primary' : 'text-gray-400 border-transparent hover:text-gray-600'}`}
                            >
                                <Receipt size={17} />
                                Boleto
                            </button>
                        )}
                    </div>
                </div>

                <div className="p-6 space-y-5">
                    {aba === 'pix' && temPix && (
                        <>
                            <p className="text-sm text-gray-500 text-center">
                                Copie o código abaixo para pagar via PIX em qualquer aplicativo habilitado.
                            </p>
                            {fatura.url_img_codigo_pix_boleto && (
                                <img
                                    src={fatura.url_img_codigo_pix_boleto}
                                    alt="QR Code PIX"
                                    className="w-44 h-44 mx-auto rounded-xl border border-gray-200 object-contain"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                            )}
                            <CopiarBox valor={fatura.codigo_pix_boleto as string} rotulo="pix" />
                        </>
                    )}

                    {aba === 'boleto' && temBoleto && (
                        <>
                            {fatura.linha_digitavel_boleto && (
                                <>
                                    <p className="text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                                        <Barcode size={16} className="text-gray-400" />
                                        Linha digitável do boleto
                                    </p>
                                    <CopiarBox valor={fatura.linha_digitavel_boleto} rotulo="boleto" />
                                </>
                            )}
                            {linkBoleto && (
                                <a
                                    href={linkBoleto}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-full text-sm font-bold text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
                                >
                                    <Receipt size={18} />
                                    Abrir boleto (PDF)
                                    <ExternalLink size={14} />
                                </a>
                            )}
                        </>
                    )}

                    {!temPix && !temBoleto && (
                        <p className="text-sm text-gray-500 text-center py-4">
                            O código de pagamento deste mês ainda não foi emitido. Fale com o atendimento se precisar.
                        </p>
                    )}

                    {fatura.pdf_fatura && typeof fatura.pdf_fatura === 'string' && (
                        <a
                            href={fatura.pdf_fatura}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex justify-center items-center gap-2 py-2 text-xs font-semibold text-gray-400 hover:text-primary transition-colors"
                        >
                            <FileText size={14} />
                            Ver fatura da distribuidora (PDF)
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PagamentoModal;
